import { motion } from 'framer-motion';
import { ChefHat, Sparkles, Mic, AlertTriangle, FileText } from 'lucide-react';
import robotWaiterImage from '@/assets/lelah.gif';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'wouter';

interface WelcomeOverlayProps {
  restaurantName: string;
  onDismiss: () => void;
}

export function WelcomeOverlay({ restaurantName, onDismiss }: WelcomeOverlayProps) {
  const { t } = useLanguage();

  const handleInteraction = (e: React.MouseEvent | React.TouchEvent) => {
    // Don't dismiss if clicking on the terms link
    if ((e.target as HTMLElement).closest('a')) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    onDismiss();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-4"
      onClick={handleInteraction}
      onTouchEnd={handleInteraction}
      data-testid="welcome-overlay"
    >
      {/* Blurred backdrop */}
      <div className="absolute inset-0 bg-background/90 backdrop-blur-lg" />

      {/* Content */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.4, ease: "easeOut" }}
        className="relative z-10 flex flex-col items-center gap-4 px-6 py-6 max-w-md mx-4"
      >
        {/* Robot waiter with glow effect */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5, type: "spring", bounce: 0.5 }}
          className="relative"
        >
          {/* Glow ring */}
          <div className="absolute inset-0 rounded-full bg-primary/30 blur-2xl scale-125" />

          {/* Robot image */}
          <div className="relative w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-primary to-primary/80 shadow-2xl">
            <img
              src={robotWaiterImage}
              alt="AI Waiter"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Sparkle decorations */}
          <motion.div
            animate={{
              rotate: [0, 360],
              scale: [1, 1.1, 1]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute -top-1 -right-1"
          >
            <Sparkles className="w-5 h-5 text-primary" fill="currentColor" />
          </motion.div>
        </motion.div>

        {/* Welcome text */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="text-center space-y-2"
        >
          <div className="flex items-center justify-center gap-2 text-primary">
            <ChefHat className="w-4 h-4" />
            <span className="text-sm font-medium uppercase tracking-wider">
              {restaurantName}
            </span>
          </div>

          <h1 className="text-2xl font-bold text-foreground">
            {t('welcome')}
          </h1>

          <p className="text-muted-foreground text-sm">
            {t('meetYourAIWaiter')}
          </p>
        </motion.div>

        {/* Instructions Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
          className="w-full space-y-3 bg-muted/50 rounded-xl p-4"
        >
          {/* Microphone instruction */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
              <Mic className="w-3.5 h-3.5 text-primary" />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              To talk with <span className="font-semibold text-foreground">Lelah</span>, please allow microphone access when your browser asks.
            </p>
          </div>

          {/* AI Warning */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              While Lelah is designed to be helpful, she may occasionally make mistakes or misunderstand requests. If anything sounds off, feel free to ask again or speak with a staff member.
            </p>
          </div>

          {/* Terms */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="w-3.5 h-3.5 text-primary" />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              By continuing, you agree to our{' '}
              <Link
                href="/customer-terms"
                className="text-primary underline hover:text-primary/80 font-medium"
                onClick={(e) => e.stopPropagation()}
              >
                Terms of Use
              </Link>.
            </p>
          </div>
        </motion.div>

        {/* Tap to start button */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.7, duration: 0.4, type: "spring" }}
          whileTap={{ scale: 0.95 }}
          className="relative mt-2"
        >
          {/* Pulsing ring animation */}
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.5, 0, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute inset-0 rounded-full bg-primary/30"
          />

          {/* Button */}
          <button
            className="relative bg-primary text-primary-foreground px-8 py-3 rounded-full text-base font-semibold shadow-lg"
            data-testid="button-tap-to-start"
          >
            {t('tapToStart')}
          </button>
        </motion.div>

        {/* Helper text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.4 }}
          className="text-xs text-muted-foreground text-center"
        >
          {t('tapAnywhereToContinue')}
        </motion.p>
      </motion.div>
    </motion.div>
  );
}
